import { Link } from 'react-router-dom';
import { Check, ExternalLink, Minus } from 'lucide-react';
import {
  ROLES, ROLE_PERMISSION_SEED, ALL_PERMISSION_CODES, PERMISSION_DESCRIPTION,
  PERMISSION_MODULE_CODE, MODULES, type RoleCode, type PermissionCode,
} from '@pms/shared';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { PERMISSIONS } from '@pms/shared';

const ROLE_ORDER: RoleCode[] = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.TEAM_LEADER, ROLES.USER];
const ROLE_LABEL: Record<RoleCode, string> = {
  [ROLES.SUPER_ADMIN]: 'Super Admin',
  [ROLES.ADMIN]: 'Admin',
  [ROLES.MANAGER]: 'Manager',
  [ROLES.TEAM_LEADER]: 'Team Leader',
  [ROLES.USER]: 'User',
};

const TASK_PERMS = ALL_PERMISSION_CODES.filter(
  (p) => PERMISSION_MODULE_CODE[p] === MODULES.TASK || PERMISSION_MODULE_CODE[p] === MODULES.ADDITIONAL_TASK,
);

const ROLE_SETS = new Map<RoleCode, Set<PermissionCode>>(
  ROLE_ORDER.map((r) => [r, new Set(ROLE_PERMISSION_SEED[r])]),
);

export function RolePermissionsTab() {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Role Permissions</h2>
          <p className="text-sm text-muted-foreground">Default task permissions per role (read-only).</p>
        </div>
        <PermissionGate permission={PERMISSIONS.ROLES_MANAGE}>
          <Link to="/system/roles" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
            Manage at Roles &amp; Permissions <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </PermissionGate>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
        <table className="w-full text-sm">
          <thead className="bg-primary text-xs uppercase tracking-wider text-primary-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Permission</th>
              {ROLE_ORDER.map((r) => (
                <th key={r} className="px-3 py-2.5 text-center font-semibold">{ROLE_LABEL[r]}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {TASK_PERMS.map((p) => (
              <tr key={p} className="hover:bg-muted/30">
                <td className="px-4 py-3">
                  <div className="font-mono text-xs">{p}</div>
                  <div className="text-xs text-muted-foreground">{PERMISSION_DESCRIPTION[p]}</div>
                </td>
                {ROLE_ORDER.map((r) => (
                  <td key={r} className="px-3 py-3 text-center">
                    {ROLE_SETS.get(r)!.has(p)
                      ? <Check className="mx-auto h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      : <Minus className="mx-auto h-4 w-4 text-muted-foreground/40" />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Shows the seeded defaults. Live role-permission mappings are managed centrally on the Roles &amp; Permissions page.
      </p>
    </div>
  );
}
